import { reflectionsApi } from "@/api";
import {
  cancelReflectionReminder,
  scheduleReflectionReminder,
  scheduleWeeklyReflectionReminders,
} from "@/lib/notifications";
import { useAuthStore } from "@/state/auth-store";
import type { ReflectionFrequency } from "@/types";

/** Local time every reflection reminder fires (8:00 PM) unless overridden. */
const REMINDER_HOUR = 20;
const REMINDER_MINUTE = 0;

/** "weekly" fires on Saturday. JS getDay(): 0 = Sunday … 6 = Saturday. */
const WEEKLY_DAY = 6;

/** expo-notifications weekday (1 = Sunday … 7 = Saturday) from a JS getDay(). */
function toExpoWeekday(jsDay: number): number {
  return jsDay + 1;
}

/** Parse a stored "HH:MM" into {hour, minute}, falling back to 8:00 PM. */
function parseReminderTime(time: string | null | undefined): {
  hour: number;
  minute: number;
} {
  if (time && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
    const [h, m] = time.split(":");
    return { hour: Number(h), minute: Number(m) };
  }
  return { hour: REMINDER_HOUR, minute: REMINDER_MINUTE };
}

/**
 * Reconcile the reflection reminder with the user's current cadence. Safe to
 * call often (on app foreground, cadence change, or after logging a reflection).
 *
 *   off      → cancel everything.
 *   evenings → a one-shot 8 PM *today* reminder, re-armed on each open. It fires
 *              only when it's genuinely useful (see below), so silent days stay
 *              quiet. A repeating daily trigger would nag even on days the user
 *              never opened the app.
 *   weekly   → a repeating Saturday 8 PM reminder (fires even if the app is
 *              closed all week — that's the point of a weekly nudge).
 *   custom   → repeating reminders on the user's chosen weekdays + time.
 *
 * The one-shot "evenings" reminder is armed only if, at call time:
 *   - notifications are permitted (enforced by the scheduling primitive);
 *   - the user has not reflected today (quota `used === 0`);
 *   - it's still before 8 PM — i.e. the user was active today in time to be
 *     reminded this evening.
 */
export async function syncReflectionReminder(): Promise<void> {
  const user = useAuthStore.getState().user;
  const frequency: ReflectionFrequency = user?.reflectionFrequency ?? "evenings";

  if (frequency === "off") {
    await cancelReflectionReminder();
    return;
  }

  if (frequency === "weekly") {
    await scheduleWeeklyReflectionReminders([
      {
        weekday: toExpoWeekday(WEEKLY_DAY),
        hour: REMINDER_HOUR,
        minute: REMINDER_MINUTE,
      },
    ]);
    return;
  }

  if (frequency === "custom") {
    const { hour, minute } = parseReminderTime(user?.reflectionReminderTime);
    const days = (user?.reflectionReminderDays ?? []).filter(
      (d) => Number.isInteger(d) && d >= 0 && d <= 6,
    );
    if (days.length === 0) {
      // Custom with no days chosen yet — nothing to fire.
      await cancelReflectionReminder();
      return;
    }
    await scheduleWeeklyReflectionReminders(
      days.map((d) => ({ weekday: toExpoWeekday(d), hour, minute })),
    );
    return;
  }

  // ── evenings ──────────────────────────────────────────────────────────────
  const now = new Date();
  const target = new Date();
  target.setHours(REMINDER_HOUR, REMINDER_MINUTE, 0, 0);

  // Past 8 PM already: too late for tonight. Their next open arms tomorrow.
  if (now.getTime() >= target.getTime()) {
    await cancelReflectionReminder();
    return;
  }

  try {
    const quota = await reflectionsApi.quota();
    if (quota.usedToday > 0) {
      // Already reflected today — no nudge needed. (`used` is monthly, so it
      // must NOT be used here or the reminder would stop for the whole month.)
      await cancelReflectionReminder();
      return;
    }
  } catch {
    // If the quota is unreachable, err toward scheduling; a reminder is low-cost.
  }

  await scheduleReflectionReminder(target);
}
